/**
 * Default height and clamp per chart type, px — one table shared by the
 * chart components and InsightChart's loading / empty / error
 * placeholder. The placeholder used a flat 250 regardless of type, so a
 * sankey (400) or a pie (260) jumped when its data landed.
 *
 * Author-set heights are clamped, never shrunk for width. The heatmap
 * grows with its rows and computes its own height; its entry is the
 * floor that computation starts from.
 */

export interface HeightRule {
  def: number;
  min: number;
  max: number;
}

const STANDARD: HeightRule = { def: 250, min: 120, max: 400 };
const TALL: HeightRule = { def: 280, min: 160, max: 400 };

export const CHART_HEIGHTS: Readonly<Record<string, HeightRule>> = {
  line: STANDARD,
  bar: STANDARD,
  stacked_bar: STANDARD,
  area: STANDARD,
  scatter: STANDARD,
  histogram: STANDARD,
  boxplot: STANDARD,
  gauge: { def: 250, min: 160, max: 400 },
  mixed: TALL,
  waterfall: TALL,
  candlestick: TALL,
  punchcard: TALL,
  funnel: TALL,
  polar_bar: TALL,
  radar: TALL,
  pie: { def: 260, min: 160, max: 400 },
  donut: { def: 260, min: 160, max: 400 },
  sunburst: { def: 320, min: 200, max: 400 },
  calendar: { def: 220, min: 160, max: 400 },
  sankey: { def: 400, min: 200, max: 600 },
  tree: { def: 400, min: 200, max: 800 },
  heatmap: { def: 250, min: 250, max: 800 },
};

/** The chart's height in px: the author's value clamped, else the type default. */
export function resolveChartHeight(type: string, height?: number): number {
  const rule = CHART_HEIGHTS[type] ?? STANDARD;
  return Math.min(Math.max(height ?? rule.def, rule.min), rule.max);
}
